import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription, catchError, of, switchMap, timer } from 'rxjs';
import {
  AuthorizeSipResponse,
  SipPayApiEnv,
  SipPayService,
  WebhookSiteRequest,
} from 'src/app/services/sip-pay.service';

type AppScreen = 'form' | 'waiting' | 'result';

interface PaymentResult {
  success: boolean;
  message: string;
  code?: string;
  source?: 'webhook' | 'order_status' | 'authorize' | 'timeout';
}

const DEFAULT_WEBHOOK_TOKEN = '6f23ffe5-7271-4231-8c41-db6de7cff871';
const WEBHOOK_POLL_MS = 2000;
const ORDER_STATUS_POLL_MS = 3000;
const EXPIRES_GRACE_MS = 15_000;

@Component({
  standalone: false,
  selector: 'app-form-sip-pay',
  templateUrl: './form-sip-pay.component.html',
  styleUrls: [
    '../form-saved-card/form-saved-card.component.css',
    './form-sip-pay.component.css',
  ],
})
export class FormSipPayComponent implements OnInit, OnDestroy {
  myForm: FormGroup;
  appScreen: AppScreen = 'form';
  isLoading = false;
  errorMessage = '';

  uniqueIdentifier = '';
  expiresAtIso = '';
  waitingCountdownLabel = '';
  /** Texto en pantalla de espera: webhook.site o fallback GET orden. */
  waitingPollLabel = 'webhook.site';
  paymentResult: PaymentResult | null = null;

  private pollSub: Subscription | null = null;
  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private authorizeStartedAtMs = 0;
  private orderIdInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly sipPayService: SipPayService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.myForm = this.formBuilder.group({
      apiEnvironment: ['DEV' as SipPayApiEnv],
      apiKey: ['OCC-40467ba5-f254-4cf0-937e-9f42a206cb3f', Validators.required],
      secret: ['93ffbb5b3e574d76a89b43ded9daa0e4', Validators.required],
      webhookToken: [DEFAULT_WEBHOOK_TOKEN, Validators.required],
      orderNumber: [this.sipPayService.createOrderNumber(), Validators.required],
      documentType: ['DNI'],
      documentNumber: ['51014633', Validators.required],
      phone: ['980200821', Validators.required],
      currencyCode: ['PEN'],
      countryCode: ['PER'],
      amount: [10, [Validators.required, Validators.min(1)]],
      language: ['es_PE'],
      customerIdentifier: ['sergio4421', Validators.required],
      name: ['Jhony', Validators.required],
      lastName: ['Pacheco', Validators.required],
      email: ['test@test.com', Validators.required],
      customerPhone: ['998910021', Validators.required],
      addressCountry: ['PER'],
      addressLevel1: ['LIMA'],
      addressLevel2: ['LIMA'],
      addressLevel3: ['SAN ISIDRO'],
      addressLine1: ['Av Zeta 1'],
      addressZip: ['051014'],
      productName: ['Heladera'],
      productSku: ['1234567890'],
      productCategory: ['Electrodomestico'],
    });
  }

  ngOnInit(): void {
    this.startOrderIdInterval();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.stopCountdown();
    this.stopOrderIdInterval();
  }

  get selectedApiEnv(): SipPayApiEnv {
    return this.myForm.get('apiEnvironment')?.value ?? 'DEV';
  }

  get selectedApiBaseUrl(): string {
    return this.sipPayService.apiBaseUrl(this.selectedApiEnv);
  }

  get webhookSiteUrl(): string {
    const token = this.myForm.get('webhookToken')?.value || DEFAULT_WEBHOOK_TOKEN;
    return `https://webhook.site/#!/view/${token}`;
  }

  get currentTimeLabel(): string {
    const now = new Date();
    return `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`;
  }

  openSimulator(): void {
    const path = `${window.location.origin}/agora-simulator-control`;
    window.open(path, '_blank', 'noopener,noreferrer');
  }

  openWebhookSite(): void {
    window.open(this.webhookSiteUrl, '_blank', 'noopener,noreferrer');
  }

  resetFlow(): void {
    this.stopPolling();
    this.stopCountdown();
    this.appScreen = 'form';
    this.paymentResult = null;
    this.uniqueIdentifier = '';
    this.expiresAtIso = '';
    this.errorMessage = '';
    this.myForm.get('orderNumber')?.setValue(this.sipPayService.createOrderNumber(), { emitEvent: false });
    this.startOrderIdInterval();
  }

  startSipPayment(): void {
    if (this.myForm.invalid) {
      this.errorMessage = 'Completa los campos requeridos.';
      return;
    }

    this.stopOrderIdInterval();
    this.isLoading = true;
    this.errorMessage = '';
    this.paymentResult = null;
    const form = this.myForm.value;
    const transactionId = `sip-demo-${form.orderNumber}-${Date.now()}`;
    const body = this.sipPayService.buildAuthorizeBody({
      orderNumber: form.orderNumber,
      amount: Number(form.amount),
      currencyCode: form.currencyCode,
      countryCode: form.countryCode,
      language: form.language,
      documentType: form.documentType,
      documentNumber: form.documentNumber,
      sipPhone: form.phone,
      customerIdentifier: form.customerIdentifier,
      name: form.name,
      lastName: form.lastName,
      email: form.email,
      customerPhone: form.customerPhone,
      addressCountry: form.addressCountry,
      addressLevel1: form.addressLevel1,
      addressLevel2: form.addressLevel2,
      addressLevel3: form.addressLevel3,
      addressLine1: form.addressLine1,
      addressZip: form.addressZip,
      productName: form.productName,
      productSku: form.productSku,
      productCategory: form.productCategory,
    });

    this.authorizeStartedAtMs = Date.now();

    this.sipPayService
      .authorizeSip(body, form.apiKey, form.secret, form.apiEnvironment, transactionId)
      .pipe(
        catchError((err) => {
          const msg =
            err?.error?.message?.text ||
            err?.error?.message?.code ||
            err?.message ||
            'Error al autorizar SIP';
          return of({
            success: false,
            message: { code: 'HTTP_ERROR', text: String(msg) },
            data: { code: 'ERROR', message: String(msg) },
          } as AuthorizeSipResponse);
        }),
      )
      .subscribe((resp) => {
        this.isLoading = false;
        const code = String(resp?.data?.code || '').toUpperCase();

        if (code !== 'PENDING') {
          this.appScreen = 'result';
          this.paymentResult = {
            success: resp.success && code === 'AUTHORIZED',
            message: resp.data?.message || resp.message?.text || code,
            code,
            source: 'authorize',
          };
          this.cdr.markForCheck();
          return;
        }

        this.uniqueIdentifier =
          resp.data?.processor?.order?.uniqueIdentifier || '';
        this.expiresAtIso = resp.data?.expiresAt || '';
        this.appScreen = 'waiting';
        this.startCountdown();
        this.waitingPollLabel = 'webhook.site';
        this.startWaitingPoll();
        this.cdr.markForCheck();
      });
  }

  private getExpiresAtMs(): number {
    return this.expiresAtIso
      ? new Date(this.expiresAtIso).getTime() + EXPIRES_GRACE_MS
      : Date.now() + 195_000;
  }

  /** 1) Poll webhook (proxy local en ng serve); si falla, 2) GET orden cada 3 s. */
  private startWaitingPoll(): void {
    this.startWebhookPolling();
  }

  private startWebhookPolling(): void {
    this.stopPolling();
    const token = this.myForm.get('webhookToken')?.value || DEFAULT_WEBHOOK_TOKEN;
    const expiresMs = this.getExpiresAtMs();

    this.pollSub = timer(0, WEBHOOK_POLL_MS)
      .pipe(
        switchMap(() => {
          if (Date.now() > expiresMs) {
            return of({ type: 'timeout' as const });
          }
          return this.sipPayService.listWebhookRequests(token).pipe(
            switchMap((requests) => of({ type: 'webhook' as const, requests })),
            catchError(() => of({ type: 'webhook_unreachable' as const })),
          );
        }),
      )
      .subscribe((event) => {
        if (event.type === 'timeout') {
          this.finishWithTimeout();
          return;
        }
        if (event.type === 'webhook_unreachable') {
          this.waitingPollLabel = 'GET order/operation/orders (fallback)';
          this.startOrderStatusPolling();
          return;
        }
        const match = this.findMatchingWebhook(event.requests);
        if (match) {
          this.finishFromWebhook(match);
        }
      });
  }

  private startOrderStatusPolling(): void {
    this.stopPolling();
    const form = this.myForm.value;
    const expiresMs = this.getExpiresAtMs();

    this.pollSub = timer(0, ORDER_STATUS_POLL_MS)
      .pipe(
        switchMap(() => {
          if (Date.now() > expiresMs) {
            return of(null);
          }
          return this.sipPayService
            .queryOrderStatus(this.uniqueIdentifier, form.apiKey, form.apiEnvironment)
            .pipe(catchError(() => of(undefined)));
        }),
      )
      .subscribe((body) => {
        if (body === null) {
          this.finishWithTimeout();
          return;
        }
        if (!body) {
          return;
        }
        const parsed = this.sipPayService.parseTerminalFromOrderBody(body);
        if (parsed.isTerminal) {
          this.finishFromOrderStatus(parsed.code, parsed.success);
        }
      });
  }

  private findMatchingWebhook(requests: WebhookSiteRequest[]): WebhookSiteRequest | null {
    const afterMs = this.authorizeStartedAtMs - 5000;
    for (const req of requests) {
      const created = new Date(req.created_at).getTime();
      if (created < afterMs) {
        continue;
      }
      if (this.sipPayService.webhookMatchesUniqueId(req.content || '', this.uniqueIdentifier)) {
        return req;
      }
    }
    return null;
  }

  private finishFromWebhook(req: WebhookSiteRequest): void {
    this.stopPolling();
    this.stopCountdown();
    const parsed = this.sipPayService.parseWebhookPayload(req.content || '');
    const code = String(parsed?.data?.code || '').toUpperCase();
    const success = code === 'AUTHORIZED' || parsed?.success === true;
    this.appScreen = 'result';
    this.paymentResult = {
      success,
      message: success ? 'Pago confirmado (sipWebHook en webhook.site)' : `Estado webhook: ${code || 'DESCONOCIDO'}`,
      code: code || undefined,
      source: 'webhook',
    };
    this.cdr.markForCheck();
  }

  private finishFromOrderStatus(code: string, success: boolean): void {
    this.stopPolling();
    this.stopCountdown();
    this.appScreen = 'result';
    this.paymentResult = {
      success,
      message: success
        ? 'Pago confirmado (consulta de orden ePago)'
        : `Estado terminal: ${code}`,
      code,
      source: 'order_status',
    };
    this.cdr.markForCheck();
  }

  private finishWithTimeout(): void {
    this.stopPolling();
    this.stopCountdown();
    this.appScreen = 'result';
    this.paymentResult = {
      success: false,
      message:
        'La orden siguió pendiente hasta expiresAt. Aprobá en el simulador, revisá webhook.site manualmente o status-event en preprocessor.',
      code: 'WAIT_TIMEOUT',
      source: 'timeout',
    };
    this.cdr.markForCheck();
  }

  private startCountdown(): void {
    this.stopCountdown();
    this.updateCountdownLabel();
    this.countdownInterval = setInterval(() => this.updateCountdownLabel(), 1000);
  }

  private updateCountdownLabel(): void {
    if (!this.expiresAtIso) {
      this.waitingCountdownLabel = '—';
      return;
    }
    const diff = new Date(this.expiresAtIso).getTime() - Date.now();
    if (diff <= 0) {
      this.waitingCountdownLabel = '0:00';
      return;
    }
    const sec = Math.ceil(diff / 1000);
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    this.waitingCountdownLabel = `${m}:${String(s).padStart(2, '0')}`;
    this.cdr.markForCheck();
  }

  private stopCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private stopPolling(): void {
    if (this.pollSub) {
      this.pollSub.unsubscribe();
      this.pollSub = null;
    }
  }

  private startOrderIdInterval(): void {
    this.stopOrderIdInterval();
    this.orderIdInterval = setInterval(() => {
      if (this.appScreen === 'form' && !this.isLoading) {
        this.myForm.get('orderNumber')?.setValue(this.sipPayService.createOrderNumber(), {
          emitEvent: false,
        });
      }
    }, 1000);
  }

  private stopOrderIdInterval(): void {
    if (this.orderIdInterval) {
      clearInterval(this.orderIdInterval);
      this.orderIdInterval = null;
    }
  }
}
